import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSEO } from "../../lib/seo";
import { toast } from "sonner";
import { MapPin, Star, CurrencyDollar, Globe, GraduationCap, ChatCircle, LinkSimple } from "@phosphor-icons/react";

export default function VirtualFreelancerProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO(p ? { title: `${p.name} — ${p.headline}`, description: p.bio?.slice(0, 155), path: `/virtual/freelancers/${id}` } : { title: "Loading…" });

  useEffect(() => {
    api.get(`/virtual/profiles/${id}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
  }, [id]);

  const startChat = async () => {
    if (!user) { navigate(`/login?redirect=/virtual/freelancers/${id}`); return; }
    try {
      const { data } = await api.post("/conversations", { other_user_id: id });
      navigate(`/chat/${data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start chat");
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!p) return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-2xl mx-auto px-4 text-center">
        <h1 className="font-display text-4xl mb-3">Freelancer not found</h1>
        <Link to="/virtual/freelancers" className="text-brand font-semibold">← Browse freelancers</Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <article className="pt-24 pb-16 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/virtual/freelancers" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 hover:text-slate-900">← Browse freelancers</Link>

        <div className="grid md:grid-cols-3 gap-8 mt-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-4">
              {p.picture ? <img src={p.picture} alt={p.name} className="w-20 h-20 rounded-full" /> : <div className="w-20 h-20 bg-slate-200 rounded-full" />}
              <div>
                <h1 className="font-display text-3xl">{p.name}</h1>
                <div className="text-slate-600 mt-1">{p.headline}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600 mb-6">
              <span className="flex items-center gap-1"><MapPin size={14} weight="bold" /> {p.city ? `${p.city}, ` : ""}{p.country}</span>
              <span className="flex items-center gap-1"><CurrencyDollar size={14} weight="bold" /> ${p.hourly_rate}/hr CAD</span>
              <span className="flex items-center gap-1"><Globe size={14} weight="bold" /> {(p.languages || []).join(", ")}</span>
              {p.years_experience > 0 && <span className="flex items-center gap-1"><GraduationCap size={14} weight="bold" /> {p.years_experience} yr exp</span>}
              {p.rating > 0 && <span className="flex items-center gap-1"><Star size={14} weight="fill" className="text-accent-red" /> {p.rating.toFixed(1)} ({p.review_count})</span>}
            </div>

            <div className="prose prose-slate max-w-none">
              <h3 className="font-display text-lg">About</h3>
              <p className="whitespace-pre-line text-base text-slate-700">{p.bio}</p>

              {p.services?.length > 0 && <>
                <h3 className="font-display text-lg mt-6">Services offered</h3>
                <div className="not-prose flex flex-wrap gap-2">
                  {p.services.map((s) => <span key={s} className="text-xs px-2 py-1 bg-orange-50 border border-orange-200 text-accent-red font-semibold">{s}</span>)}
                </div>
              </>}

              {p.skills?.length > 0 && <>
                <h3 className="font-display text-lg mt-6">Skills</h3>
                <div className="not-prose flex flex-wrap gap-2">
                  {p.skills.map((s) => <span key={s} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200">{s}</span>)}
                </div>
              </>}

              {p.certifications?.length > 0 && <>
                <h3 className="font-display text-lg mt-6">Certifications</h3>
                <ul className="not-prose space-y-1 text-sm text-slate-700">
                  {p.certifications.map((c) => <li key={c}>• {c}</li>)}
                </ul>
              </>}
            </div>
          </div>

          <aside className="md:col-span-1">
            <div className="border border-slate-200 p-5 space-y-3">
              <button onClick={startChat} className="w-full bg-brand text-white px-4 py-3 text-sm font-semibold hover:bg-[#122148] inline-flex items-center justify-center gap-2" data-testid="message-freelancer-btn">
                <ChatCircle size={16} weight="bold" /> Message {p.name.split(" ")[0]}
              </button>
              <Link to="/virtual/jobs/new" className="block w-full text-center border border-slate-300 px-4 py-3 text-sm font-semibold hover:bg-slate-50" data-testid="invite-to-job-btn">
                Invite to a project
              </Link>
              <div className="pt-3 border-t border-slate-200 space-y-2 text-sm">
                {p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-brand"><LinkSimple size={14} /> LinkedIn</a>}
                {p.portfolio_url && <a href={p.portfolio_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-brand"><LinkSimple size={14} /> Portfolio</a>}
                {p.resume_url && <a href={p.resume_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-brand"><LinkSimple size={14} /> Resume</a>}
                {p.website_url && <a href={p.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-slate-700 hover:text-brand"><LinkSimple size={14} /> Website</a>}
              </div>
            </div>
          </aside>
        </div>
      </article>
      <Footer />
    </div>
  );
}
